import { has, get, includes } from 'lodash'
import { xss } from '../../../common/security'
import { safeJSONParse } from '../../../utils'
import { locale } from '../../../plugins/i18n'
import pageInfo from '../../../common/pageInfo'
import { logger } from '../../../utils/log'
import { APP_NAMESPACE } from '../../../store'
import { mapGetters, mapMutations } from 'vuex'
import {
  generateCvvReAuthSignature,
  generateZuoraSignature,
  generateZuoraAchSignature,
  generateZuoraSepaSignature,
  updateCvvFlag
} from '../../../apis/payment'
import {
  EVENT_ORIGIN_REG_EX,
  HPM_ERROR_MAPPING,
  HpmValidationErrorMap,
  integrationTypeMap,
  needCheckCreditFormFieldList,
  payMethodMap,
  CURRENCY_INR,
  Z_HPM_IFRAME,
  Z_CONTAINER_ID
} from '../../../common/constant'
import { getEffectiveCurrency, getSetupIntentCurrency, isInrCurrency } from '../../../utils/currency'

const Zuora = window.Z
const HOSTED_PAGE_FIELD_VALIDATION_ERROR = 'HostedPageFieldValidationError'

export default {
  data() {
    return {
      zuoraHpmIsValid: true,
      resizeHpmOnIntervalRef: null,
      resizeHpmOnIntervalCt: 0,
      isDevEnv: pageInfo.isDevEnv
    }
  },
  computed: {
    ...mapGetters(APP_NAMESPACE, [
      'cvvReAuthInfo',
      'billingInfo',
      'getHpmPassthroughValForBackupPayment',
      'countryUseAdyenForCardValidation'
    ]),
    zuoraContainerId() {
      return this.isFormVisible ? Z_CONTAINER_ID : ''
    },
    isCvvReAuthRequired() {
      return this.isCvvReAuth && this.cvvReAuthInfo?.isCreditCardReAuthRequired
    },
    countryUseAdyenForCardValidationPaymentGateWay() {
      // render hpm with field_gatewayName
      const currency = getEffectiveCurrency()
      const countryUseAdyenForCardValidationGateway = get(this.countryUseAdyenForCardValidation, this.countryCode)

      return currency !== CURRENCY_INR && countryUseAdyenForCardValidationGateway
        ? countryUseAdyenForCardValidationGateway
        : this.paymentGateway
    }
  },
  methods: {
    ...mapMutations(APP_NAMESPACE, ['setCvvReAuthInfo']),
    resizeHpm() {
      document.getElementById(Z_HPM_IFRAME) && Zuora.post(Z_HPM_IFRAME, 'resize')
    },
    resetFormState() {
      this.formLoading = false
      setTimeout(() => this.resizeHpm(), 100)
    },
    resizeHpmOnInterval() {
      // resolves potential issue when the HPM container height has not been properly resized on load
      this.resizeHpmOnIntervalCt = 0
      this.resizeHpmOnIntervalRef = setInterval(() => {
        this.resizeHpmOnIntervalCt++
        const zIframe = document.getElementById(Z_HPM_IFRAME)
        if (zIframe && zIframe.clientHeight > 700) {
          this.resizeHpm()
        } else if (this.resizeHpmOnIntervalCt >= 5) {
          clearInterval(this.resizeHpmOnIntervalRef)
        }
      }, 1000)
    },
    appendPaymentError(msg) {
      if (includes(this.paymentError, msg) === false) {
        this.paymentError += msg
      }
    },
    polishMsg(data) {
      const processMsgObj = (msgObj) => {
        let outputMsg
        const msg = msgObj.errorMessage
        const isHpmFieldValidationError = msgObj.errorCode === HOSTED_PAGE_FIELD_VALIDATION_ERROR

        if (isHpmFieldValidationError) {
          const objKeys = Object.keys(msgObj)
          const errorFieldName = objKeys.filter(function (key) {
            return key.indexOf('errorField_') === 0
          })[0]
          const errorMappingKey = HPM_ERROR_MAPPING[errorFieldName]
          if (errorMappingKey) {
            outputMsg = this.errorMapping[errorMappingKey]
          }
        } else {
          const searchKeyword = function (keyword) {
            return msgObj.errorCode?.indexOf(keyword) !== -1 || msg?.indexOf(keyword) !== -1
          }
          for (const key in this.errorMapping) {
            if (searchKeyword(key)) {
              outputMsg = this.errorMapping[key]
              break
            }
          }
        }
        return outputMsg || 'error.billing.stripe.invalid_request'
      }
      const processMsgStr = (msg) => {
        let isFind = false
        const searchKeyword = function (keyword) {
          return msg?.indexOf(keyword) !== -1
        }
        for (const key in this.errorMapping) {
          if (searchKeyword(key)) {
            msg = this.errorMapping[key]
            isFind = true
            break
          }
        }
        return isFind ? msg : xss(msg)
      }
      if (typeof data === 'object') {
        return this.__$t(processMsgObj(data))
      } else if (typeof data === 'string') {
        return this.__$t(processMsgStr(data))
      }
    },
    checkZHpmFormStyleWithMessage(options = {}) {
      const { key, code } = options
      if (code === '001' && needCheckCreditFormFieldList.includes(key)) {
        logger.info(`expand zuora hpm form field with key: ${key}`)
        this.toggleZHpmForm(true)
      }
    },
    handlerReCaptchaCallBack() {
      this.toggleZHpmForm(true)
    },
    handleValidationError(key, code, message, rawGatewayInfo) {
      let msg = this.polishMsg(message) || this.__$t('billing.unknown_error')

      logger.info(`ValidationError: key: ${key} | code: ${code} | message: ${message}`)

      // It is necessary to judge whether the zuora form needs to be expanded according to the current error code and key
      this.checkZHpmFormStyleWithMessage({ key, code })

      // '001' means required field missing
      if (code === '001') {
        msg += ': ' + (this.__$t(this.errorMapping[key]) || key) + '<br>'
      } else if (code === '007') {
        logger.info('need recaptcha')
        // Customize the error message if the end-user doesn't resolve the challenge of Google reCAPTCHA Enterprise Interactive Test version
        this.handlerReCaptchaCallBack()
      } else if (code === 'unknown' && rawGatewayInfo) {
        // Customize the error message based on the rawGatewayInfo
        logger.error('error gateway', message)
      }

      this.appendPaymentError(msg)

      if (this.paymentError) {
        this.validateInstance.solved()
      }
    },
    async validateHpmForm(paymentType = payMethodMap.credit) {
      const paymentTypeStr = paymentType !== payMethodMap.credit ? `${paymentType} bank transfer` : paymentType
      return new Promise((resolve, reject) => {
        if (paymentType === payMethodMap.credit) {
          this.setHpmHiddenFieldValues()
        }
        this.clearPaymentError()
        this.validateInstance.resetState()
        const validFail = () => {
          logger.error(`alidateHpmForm ${paymentTypeStr} request form validation error`)
          reject()
        }
        // if trigger the handleValidationError, should execute callback
        this.validateInstance.ready(validFail)
        Zuora.validate((res) => (res.success ? resolve() : validFail()))
      })
    },
    zuoraOnLoadEventListener() {
      logger.log('HPM is being loaded.........................................')
      this.resetFormState()
      this.resizeHpmOnInterval()
    },
    removeZuoraHpmLayer() {
      this.$emit('loading-close')
      this.resizeHpm()
      if (isInrCurrency()) {
        const zIframe = document.getElementById(Z_HPM_IFRAME)
        zIframe?.scrollIntoView({ behavior: 'smooth' })
      }
    },
    generateSignature() {
      if (this.isCvvReAuthRequired) {
        return generateCvvReAuthSignature()
      } else {
        return generateZuoraSignature()
      }
    },
    async generateHpmParams(payMethod = payMethodMap.credit) {
      const currency = getEffectiveCurrency()
      const setupIntentCurrency = getSetupIntentCurrency()
      const zuoraInfo =
        payMethod === payMethodMap.credit
          ? await this.generateSignature()
          : payMethod === payMethodMap.ach
            ? await generateZuoraAchSignature()
            : await generateZuoraSepaSignature()
      const { key, token, tenantId, signature, ZoomPaymentPageID, ZuoraPaymentPageURL, ZoomPaymentCallbackHost } =
        zuoraInfo
      const commonFormParams = {
        key,
        token,
        tenantId,
        signature,
        locale,
        id: ZoomPaymentPageID,
        url: ZuoraPaymentPageURL,
        field_passthrough2: 'ZBilling.callback',
        field_passthrough3: ZoomPaymentCallbackHost?.replace('https://', ''),
        field_passthrough4: currency,
        field_currency: setupIntentCurrency,
        style: 'inline',
        retainValues: true,
        submitEnabled: false
      }
      let zuoraFormParams = {}
      if (payMethod === payMethodMap.ach || payMethod === payMethodMap.sepa) {
        const paymentGateway = this.billingInfoConfig.bankTransfer?.achGatewayName
        zuoraFormParams = {
          ...commonFormParams,
          paymentGateway: paymentGateway,
          field_passthrough1: paymentGateway,
          field_passthrough5: payMethod === payMethodMap.ach ? 'isAchPay' : 'isSepaPay'
        }
      } else {
        const creditCardGateWay = this.paymentGateway
        const countryUseAdyenForCardValidationPaymentGateWay = this.countryUseAdyenForCardValidationPaymentGateWay
        const DEFAULT_ZUORA_FORM_PARAMS = {
          ...commonFormParams,
          paymentGateway: countryUseAdyenForCardValidationPaymentGateWay, // render hpm with field_gatewayName
          field_passthrough1: creditCardGateWay, // place order will use payment gateway
          field_passthrough5: 'isCreditCardPay',
          field_passthrough14: (!this.isDesktop && 'opc-mobile') || '',
          field_passthrough15: 'isCollapsed', // collapses address fields via HPM styling
          field_accountId: this.billingInfo?.zAccountId,
          countryBlackList: this.billingInfoConfig.creditCardCountryBlackList || 'CHN',
          cardMandateEnabled: false
        }

        if (this.isCvvReAuthRequired) {
          zuoraFormParams = {
            ...DEFAULT_ZUORA_FORM_PARAMS,
            field_accountId: this.billingInfo?.zAccountId,
            field_passthrough5: 'isCreditCardReAuthAndPay_' + this.cvvReAuthInfo.accountId
          }
          this.cvvReAuthInfo.pmId && (zuoraFormParams.pmId = this.cvvReAuthInfo.pmId)
        } else {
          zuoraFormParams = {
            ...DEFAULT_ZUORA_FORM_PARAMS
          }
        }

        if (isInrCurrency()) {
          zuoraFormParams.param_gwOptions_CardMandateAmount = this.indiaMandateAmount
          zuoraFormParams.cardMandateEnabled = true
        }

        if (!this.isPrimary) {
          zuoraFormParams.field_passthrough7 = this.getHpmPassthroughValForBackupPayment(this.action)
        }
      }

      return zuoraFormParams
    },
    toggleZHpmForm(expand) {
      if (!document.getElementById(Z_HPM_IFRAME)) {
        return
      }

      Zuora.setFieldValue('passthrough15', expand ? '' : 'isCollapsed')
      this.resizeHpm()
    },
    async loadHpmIframe(payMethod = payMethodMap.credit) {
      if (this.formLoading) return

      this.formLoading = true
      this.zuoraHpmIsValid = true

      const zuoraHpmParams = await this.generateHpmParams(payMethod)

      Zuora.renderWithErrorHandler(
        zuoraHpmParams,
        this.prepopulateFields || {},
        function callback() {},
        this.handleValidationError,
        null,
        null,
        this.removeZuoraHpmLayer
      )
      document.getElementById(Z_HPM_IFRAME)?.addEventListener('load', this.zuoraOnLoadEventListener)
    },
    listenToZuoraMessage() {
      const zuoraMessageHandler = (event) => {
        // Check the origin of the event to ensure this is not a malicious attack
        const reg = this.isDevEnv ? EVENT_ORIGIN_REG_EX.DEV_DOMAINS : EVENT_ORIGIN_REG_EX.PROD_DOMAINS

        if (!reg.test(event.origin.toLowerCase())) {
          return
        }

        const data = event.data || {}

        if (data != null && data.callback) {
          const funcNameArr = data.callback.split('.')
          const callbackFun = window.ZBilling?.[funcNameArr[1]]
          callbackFun?.(data.result)
        }

        if (typeof data === 'string') {
          const parsedData = safeJSONParse(data)

          if (parsedData && has(HpmValidationErrorMap, parsedData?.errorCode)) {
            this.zuoraHpmIsValid = false
          }
        }
      }
      window.addEventListener('message', zuoraMessageHandler)
    },
    removeZuoraHPMIframeElement() {
      // avoid rerender zuora hpm form can able to listener load event
      document.getElementById(Z_HPM_IFRAME)?.remove()
    },
    reRenderPayMethod() {
      this.removeZuoraHPMIframeElement()
      this.toggleZHpmForm(false)
      this.loadHpmIframe()
    },
    async updatePayMethodAndContactForm(paymentInfo) {
      try {
        await this.updatePayMethod(paymentInfo)
      } catch (e) {
        logger.error('update payment method error', e)
        // reRender component in order to reLoad zuora Hpm
        this.reRenderPayMethod()
        throw e
      }
    },
    processCvvReAuth(success, errorMsg) {
      if (this.isCvvReAuthRequired) {
        updateCvvFlag({ success, errorMsg })
      }
    },
    async submitHpmForm(payMethod) {
      return new Promise((resolve, reject) => {
        this.validateInstance.resetState()

        window.ZBilling = window.ZBilling || {}

        // Register the Zuora payment callback function, the function should be invoked through 'ZBilling.callback'
        window.ZBilling.callback = (data) => {
          const isSuccess = data.success === 'true' || (Array.isArray(data.success) && data.success[0] === 'true')
          if (isSuccess) {
            const integrateType =
              payMethod === payMethodMap.credit
                ? integrationTypeMap.PayWithCC
                : payMethod === payMethodMap.ach
                  ? integrationTypeMap.ACH
                  : integrationTypeMap.SEPA
            const paymentMethod = {
              paymentType: payMethod,
              paymentGateway: this.paymentGateway,
              paymentResponse: JSON.stringify({ ...data, integrationType: integrateType })
            }
            if (payMethod === payMethodMap.credit) {
              paymentMethod.creditCardReAuth == this.isCvvReAuthRequired
              this.processCvvReAuth(true)
            }
            resolve(paymentMethod)
          } else {
            const msg = this.polishMsg(data) || this.__$t('billing.unknown_error')
            logger.error('zuora callback error', `msg: ${msg}`)

            if (payMethod === payMethodMap.credit) {
              this.processCvvReAuth(false, data.errorMessage)
            }

            this.appendPaymentError(msg)
            this.reRenderPayMethod()
            reject()
          }
        }

        // if trigger the handleValidationError, should execute callback (show recaptcha)
        this.validateInstance.ready(() => {
          logger.error(`submitHpmForm ${payMethod} form validation error`)
          reject()
        })
        Zuora.submit()
      })
    }
  }
}
